import ExpandIcon from '@/assets/images/Expand.svg';
import { AvatarFallback, AvatarImage, Avatar as ImageAvatar } from '@/components/ui/avatar';
import zoomContext from '@/context/zoom-context';
import { cn } from '@/lib/utils';
import { authState } from '@/vars/auth';
import { useReactiveVar } from '@apollo/client';
import type { NetworkQuality, Participant } from '@zoom/videosdk';
import { MicOff } from 'lucide-react';
import { useContext, useRef } from 'react';
import { miniScreenUserIdVar, sessionVar } from '../meetingVar';

interface AvatarProps {
  participant: Participant;
  participants: Participant[];
  style?: { [key: string]: string };
  isActive: boolean;
  className?: string;
  networkQuality?: NetworkQuality;
}

const Avatar = (props: AvatarProps) => {
  const zmClient = useContext(zoomContext);
  const miniScreenUserId = useReactiveVar(miniScreenUserIdVar);
  const { userDetails } = useReactiveVar(authState);
  const sessionData = useReactiveVar(sessionVar);
  const avatarRef = useRef<HTMLDivElement>(null);
  const { participant, style, className } = props;
  const { displayName, muted, bVideoOn, userIdentity, userId } = participant;
  const isSelf = zmClient?.getCurrentUserInfo()?.userId === participant?.userId;

  const handleSetMiniScreenUser = () => {
    miniScreenUserIdVar(
      props.participants.filter((users) => users.userId !== miniScreenUserId)[0]?.userId || null,
    );
  };

  return (
    <div
      className={cn(
        'absolute top-0 flex flex-col items-center justify-center rounded-xl overflow-hidden h-full w-full p-4',
        miniScreenUserId === userId ? ' border-gray-500 border' : '',
        className,
      )}
      style={{ ...style, backgroundColor: bVideoOn ? 'transparent' : 'transparent' }}
      ref={avatarRef}
    >
      {zmClient?.getCurrentUserInfo()?.bVideoOn && (
        <div
          className='absolute top-4 right-3 flex items-center space-x-2 text-sm rounded-xl cursor-pointer'
          onClick={handleSetMiniScreenUser}
        >
          <img src={ExpandIcon} alt='expand-icon' />
        </div>
      )}
      <div
        className={cn(
          'absolute bottom-4 right-3 flex items-center space-x-2 text-sm text-white rounded-xl',
          miniScreenUserId === userId ? 'text-xs' : '',
        )}
      >
        <span className='user-name-avatar'>{isSelf ? 'You' : displayName}</span>
        {(muted === true || muted === undefined) && <MicOff className='w-5 h-5' />}
      </div>
      {!bVideoOn && (
        <div className='w-full h-full flex items-center justify-center flex-1'>
          <ImageAvatar
            className={cn(
              'w-40 h-40  bg-[#515764] z-20',
              miniScreenUserId === userId ? 'w-14 h-14' : '',
            )}
          >
            <AvatarImage
              src={
                isSelf || userDetails?._id === userIdentity
                  ? userDetails?.profileImage || ''
                  : sessionData?.trainerIdUser?._id === userIdentity
                    ? sessionData?.trainerIdUser?.profileImage || ''
                    : ''
              }
              alt='Profile picture'
            />
            <AvatarFallback
              className={cn('text-5xl font-bold', miniScreenUserId === userId ? 'text-xl' : '')}
            >
              {displayName?.charAt(0) || ''}
            </AvatarFallback>
          </ImageAvatar>
        </div>
      )}
    </div>
  );
};

export default Avatar;
